import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sort, setSort] = useState("recent");
  const [search, setSearch] = useState("");
  const [liking, setLiking] = useState({});
  const [saving, setSaving] = useState({});
  
  // Filtro de usuário vindo do perfil
  const filterByUser = location.state?.filterByUser;
  const filterByUsername = location.state?.filterByUsername;

  const categories = [
    "Tecnologia",
    "Saúde",
    "Educação",
    "Viagem",
    "Comida",
    "Estilo",
    "Negócios",
    "Lifestyle",
    "DIY",
    "Reviews",
    "Tutorial",
    "Notícias",
    "Outro",
  ];

  const fetchPosts = async (pageNum = 1, resetPosts = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pageNum,
        limit: 10,
        sort,
        ...(search && { search }),
        ...(category && { category }),
        ...(filterByUser && { userId: filterByUser }), // Adicionar filtro de usuário
      });

      const { data } = await axios.get(
        `${API_URL}/api/blog/feed?${params}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (resetPosts || pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
      }

      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar blogs:", error);
      setLoading(false);
    }
  };

  const handleLike = async (postId, isLiked) => {
    try {
      setLiking((prev) => ({ ...prev, [postId]: true }));
      if (isLiked) {
        await axios.delete(`${API_URL}/api/blog/${postId}/like`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
      } else {
        await axios.post(
          `${API_URL}/api/blog/${postId}/like`,
          {},
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      setPosts((prev) =>
        prev.map((p) =>
          p._id === postId
            ? {
                ...p,
                userState: { ...p.userState, isLiked: !isLiked },
                stats: {
                  ...p.stats,
                  likesCount: isLiked ? p.stats.likesCount - 1 : p.stats.likesCount + 1,
                },
              }
            : p
        )
      );
    } catch (error) {
      console.error("Erro ao dar like:", error);
    } finally {
      setLiking((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleSave = async (postId, isSaved) => {
    try {
      setSaving((prev) => ({ ...prev, [postId]: true }));
      if (isSaved) {
        await axios.delete(`${API_URL}/api/blog/${postId}/save`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
      } else {
        await axios.post(
          `${API_URL}/api/blog/${postId}/save`,
          {},
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      setPosts((prev) =>
        prev.map((p) =>
          p._id === postId
            ? { ...p, userState: { ...p.userState, isSaved: !isSaved } }
            : p
        )
      );
    } catch (error) {
      console.error("Erro ao salvar post:", error);
    } finally {
      setSaving((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleDelete = async (postId) => {
    if (window.confirm("Tem certeza que deseja deletar este post?")) {
      try {
        await axios.delete(`${API_URL}/api/blog/${postId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        setPosts((prev) => prev.filter((p) => p._id !== postId));
        setSelectedArticle(null);
      } catch (error) {
        console.error("Erro ao deletar post:", error);
        alert("Erro ao deletar post");
      }
    }
  };

  const handleEdit = (post) => {
    navigate(`/blog/edit/${post._id}`);
  };

  useEffect(() => {
    setPage(1);
    fetchPosts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort, search]);

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="blog_feed_loading">
        <div className="spinner"></div>
        <p>Carregando artigos...</p>
      </div>
    );
  }

  return (
    <div className="blog_feed_container">
      {/* Banner de Filtro de Usuário */}
      {filterByUser && (
        <div className="feed_user_filter_banner">
          <div className="feed_user_filter_content">
            <span className="feed_user_filter_icon">👤</span>
            <span className="feed_user_filter_text">
              Mostrando apenas publicações de <strong>{filterByUsername || "usuário"}</strong> no Blog
            </span>
            <button
              className="feed_user_filter_clear"
              onClick={() => navigate("/blog")}
              title="Limpar filtro"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      {/* Search & Filter Bar */}
      <div className="blog_search_section">
        <input
          type="text"
          placeholder="Buscar artigos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="blog_search_input"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="blog_sort_select"
        >
          <option value="recent">Mais recentes</option>
          <option value="popular">Populares</option>
          <option value="trending">Trending</option>
        </select>
      </div>

      {/* Categories */}
      <div className="blog_categories">
        <button
          className={`blog_cat_btn ${category === "" ? "active" : ""}`}
          onClick={() => setCategory("")}
        >
          Todos
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            className={`blog_cat_btn ${category === cat ? "active" : ""}`}
            onClick={() => setCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {posts.length === 0 ? (
        <div className="blog_empty_state">
          <svg viewBox="0 0 64 64" width="64" height="64" fill="currentColor">
            <path d="M48 8H16c-4.42 0-8 3.58-8 8v32c0 4.42 3.58 8 8 8h32c4.42 0 8-3.58 8-8V16c0-4.42-3.58-8-8-8zm0 40H16V16h32v32zm-6-22H22v4h20v-4zm0-8H22v4h20v-4z" />
          </svg>
          <h3>Nenhum artigo encontrado</h3>
          <p>Comece a escrever seu primeiro artigo!</p>
          <button onClick={() => navigate("/blog/create")} className="blog_create_btn">
            Novo Artigo
          </button>
        </div>
      ) : (
        <>
          <div className="blog_posts_list">
            {posts.map((post) => (
              <div key={post._id} className="blog_post_item">
                {post.coverImage?.url && (
                  <div
                    className="blog_post_image"
                    onClick={() => setSelectedArticle(post)}
                  >
                    <img src={post.coverImage.url} alt={post.title} />
                  </div>
                )}
                <div className="blog_post_content">
                  <div className="blog_post_header">
                    <div className="blog_post_meta">
                      <Link
                        to={`/profile/${post.author.username}`}
                        className="blog_post_author"
                      >
                        <img src={post.author.picture} alt={post.author.first_name} />
                        <span>{post.author.first_name} {post.author.last_name}</span>
                      </Link>
                      <span className="blog_post_date">
                        <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                      </span>
                    </div>
                    {post.userState?.isAuthor && (
                      <div className="blog_post_actions">
                        <button
                          onClick={() => handleEdit(post)}
                          className="blog_action_btn"
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(post._id)}
                          className="blog_action_btn"
                          title="Deletar"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>

                  <h3
                    className="blog_post_title"
                    onClick={() => setSelectedArticle(post)}
                  >
                    {post.title}
                  </h3>

                  {post.excerpt && (
                    <p className="blog_post_excerpt">{post.excerpt}</p>
                  )}

                  <div className="blog_post_stats">
                    <span className="blog_stat">
                      {post.stats.viewsCount} visualizações
                    </span>
                    <span className="blog_stat">
                      {post.stats.likesCount} likes
                    </span>
                    <span className="blog_stat">
                      {post.stats.commentsCount} comentários
                    </span>
                    {post.stats.readTime && (
                      <span className="blog_stat">{post.stats.readTime} min</span>
                    )}
                  </div>

                  <div className="blog_post_tags">
                    {post.tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="blog_tag">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="blog_post_actions_bottom">
                    <button
                      onClick={() =>
                        handleLike(post._id, post.userState?.isLiked)
                      }
                      disabled={liking[post._id]}
                      className={`blog_action ${
                        post.userState?.isLiked ? "liked" : ""
                      }`}
                    >
                      👍 {post.stats.likesCount}
                    </button>
                    <button
                      onClick={() => setSelectedArticle(post)}
                      className="blog_action"
                    >
                      💬 {post.stats.commentsCount}
                    </button>
                    <button
                      onClick={() =>
                        handleSave(post._id, post.userState?.isSaved)
                      }
                      disabled={saving[post._id]}
                      className={`blog_action ${
                        post.userState?.isSaved ? "saved" : ""
                      }`}
                    >
                      🔖 {post.userState?.isSaved ? "Salvo" : "Salvar"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <button
              onClick={loadMore}
              disabled={loading}
              className="blog_load_more_btn"
            >
              {loading ? "Carregando..." : "Carregar mais"}
            </button>
          )}
        </>
      )}

      {/* Article Detail Modal */}
      {selectedArticle && (
        <BlogPostDetail
          post={selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onLike={handleLike}
          onSave={handleSave}
          user={user}
        />
      )}
    </div>
  );
}

// Componente para mostrar detalhes do artigo
function BlogPostDetail({
  post,
  onClose,
  onEdit,
  onDelete,
  onLike,
  onSave,
  user,
}) {
  const [comments, setComments] = useState(post.comments || []);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      const { data } = await axios.post(
        `${API_URL}/api/blog/${post._id}/comment`,
        { text: newComment },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setComments([...comments, data.comment]);
      setNewComment("");
    } catch (error) {
      console.error("Erro ao adicionar comentário:", error);
      alert("Erro ao adicionar comentário");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm("Deletar comentário?")) {
      try {
        await axios.delete(
          `${API_URL}/api/blog/${post._id}/comment/${commentId}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setComments(comments.filter((c) => c._id !== commentId));
      } catch (error) {
        console.error("Erro ao deletar comentário:", error);
      }
    }
  };

  return (
    <div className="blog_modal_overlay" onClick={onClose}>
      <div className="blog_modal" onClick={(e) => e.stopPropagation()}>
        <button className="blog_modal_close" onClick={onClose}>
          ✕
        </button>

        <div className="blog_modal_content">
          {post.coverImage?.url && (
            <img src={post.coverImage.url} alt={post.title} className="blog_modal_cover" />
          )}

          <div className="blog_modal_body">
            <h1 className="blog_modal_title">{post.title}</h1>

            <div className="blog_modal_meta">
              <Link
                to={`/profile/${post.author.username}`}
                className="blog_modal_author"
              >
                <img src={post.author.picture} alt={post.author.first_name} />
                <div>
                  <span className="blog_modal_author_name">
                    {post.author.first_name} {post.author.last_name}
                  </span>
                  <span className="blog_modal_author_info">
                    <Moment format="DD [de] MMMM" locale="pt-br">
                      {post.createdAt}
                    </Moment>
                    {post.stats.readTime && (
                      <> • {post.stats.readTime} min de leitura</>
                    )}
                  </span>
                </div>
              </Link>
            </div>

            {post.category && (
              <div className="blog_modal_category">{post.category}</div>
            )}

            <div className="blog_modal_text">
              {post.content}
            </div>

            {post.tags?.length > 0 && (
              <div className="blog_modal_tags">
                {post.tags.map((tag, i) => (
                  <span key={i}>#{tag}</span>
                ))}
              </div>
            )}

            <div className="blog_modal_actions">
              <button
                onClick={() => onLike(post._id, post.userState?.isLiked)}
                className={`blog_modal_action ${
                  post.userState?.isLiked ? "liked" : ""
                }`}
              >
                👍 {post.stats.likesCount}
              </button>
              <button
                onClick={() => onSave(post._id, post.userState?.isSaved)}
                className={`blog_modal_action ${
                  post.userState?.isSaved ? "saved" : ""
                }`}
              >
                🔖 {post.userState?.isSaved ? "Salvo" : "Salvar"}
              </button>
              {post.userState?.isAuthor && (
                <>
                  <button onClick={() => onEdit(post)} className="blog_modal_action">
                    ✏️ Editar
                  </button>
                  <button onClick={() => onDelete(post._id)} className="blog_modal_action danger">
                    🗑️ Deletar
                  </button>
                </>
              )}
            </div>

            {/* Comments Section */}
            {post.allowComments && (
              <div className="blog_comments_section">
                <h3>Comentários ({comments.length})</h3>

                <div className="blog_comment_form">
                  <img src={user.picture} alt={user.first_name} />
                  <div className="blog_comment_input_wrapper">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Deixe um comentário..."
                      rows="3"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={submittingComment || !newComment.trim()}
                      className="blog_comment_btn"
                    >
                      {submittingComment ? "Postando..." : "Comentar"}
                    </button>
                  </div>
                </div>

                <div className="blog_comments_list">
                  {comments.map((comment) => (
                    <div key={comment._id} className="blog_comment">
                      <img
                        src={comment.commentBy.picture}
                        alt={comment.commentBy.first_name}
                      />
                      <div className="blog_comment_content">
                        <div className="blog_comment_header">
                          <strong>{comment.commentBy.first_name}</strong>
                          <span className="blog_comment_date">
                            <Moment fromNow locale="pt-br">
                              {comment.commentAt}
                            </Moment>
                          </span>
                        </div>
                        <p className="blog_comment_text">{comment.text}</p>
                        {(comment.commentBy._id === user._id ||
                          post.author._id === user._id) && (
                          <button
                            onClick={() => handleDeleteComment(comment._id)}
                            className="blog_comment_delete"
                          >
                            Deletar
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}